/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ScimService {
  /**
   * Delete SCIM group by ID
   * Design note:
   * - SCIM Group IDs are identical to our internal subscription IDs.
   * We do not create a separate SCIM identifier.
   * - The displayName encodes: LocalStack-{plan}-{emulator}-{subscription_id}
   * and is only used to locate the subscription on POST.
   *
   * @param groupId The unique SCIM group (subscription) ID to delete.
   * @returns void
   * @throws ApiError
   */
  public static deleteScimV2Groups(groupId: string): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/scim/v2/Groups/{group_id}',
      path: {
        group_id: groupId,
      },
      errors: {
        401: `Unauthorized - SCIM token missing`,
        403: `Forbidden - Invalid SCIM token`,
        404: `Group not found`,
      },
    });
  }

  /**
   * Get SCIM group by ID
   * @param groupId The unique SCIM group (subscription) ID
   * @returns any SCIM group found
   * @throws ApiError
   */
  public static getScimV2Groups(groupId: string): CancelablePromise<{
    schemas: Array<string>;
    id: string;
    displayName: string;
    members: Array<{
      value?: string;
      display?: string;
    }>;
    meta: {
      resourceType?: string;
    };
  }> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/scim/v2/Groups/{group_id}',
      path: {
        group_id: groupId,
      },
      errors: {
        401: `Unauthorized - SCIM token missing`,
        403: `Forbidden - Invalid SCIM token`,
        404: `Group not found`,
      },
    });
  }

  /**
   * Update SCIM group members via PATCH (add, remove, or replace members)
   * * The SCIM Group `id` equals our internal `subscription.id`.
   * * **Schemas requirement:** Per RFC 7643 §3, the request body **MUST** include `schemas`
   * and it **MUST** contain the PatchOp schema URI
   * `urn:ietf:params:scim:api:messages:2.0:PatchOp`. Missing/invalid schemas result in `400`.
   * * **Content type:** Clients SHOULD send `Content-Type: application/scim+json`
   * and `Accept: application/scim+json`.
   * * **Supported paths:**
   * - `members`
   * - `members[value eq "<user_id>"]`
   * * **Operation variants:**
   * - **replace**: value may be `{"members":[...]}`, `[...]`, or `null` (clear all).
   * `{}` or `{"displayName":...}` is treated as a no-op (group rename is ignored).
   * - **add**: path must be `members` (or empty); value may be `{"members":[...]}` or `[...]`.
   * Empty/absent members is a no-op.
   * - **remove**: `members[value eq "<user_id>"]` (single),
   * or `members` with `{"members":[...]}` / `[...]` / `null` (clear all).
   * * **Capacity enforcement & ordering (PUT parity):**
   * - For **replace**, we compute the projected final size after removals:
   * `projected = current - remove + add`. We return `409` only if `projected > seats`,
   * allowing 1-for-1 swaps at capacity.
   * - For **add**, we use a **soft preflight**: we only block when the group is already
   * at capacity; otherwise we attempt per-user adds guarded by an authoritative seat
   * check and a numeric guard. This lets uniqueness races surface correctly and enables
   * best-effort rollback on partial failures.
   * * **Full replace semantics:** `replace` on `members` replaces membership entirely.
   * If `members` is omitted or set to `null`, all existing members are removed.
   * * **Idempotency & rollback:**
   * - Adding users who are already members is a no-op (de-duplicated by user id).
   * - On mid-operation failure, we best-effort roll back any users added by that operation,
   * while keeping successful removals.
   * * **Prefer header:** If the client sends `Prefer: return=minimal`, the server MAY respond
   * with `204 No Content`. The `Content-Type` header will still be set to `application/scim+json`.
   * * **IdP compatibility:** IdPs issue PATCH for group push deltas; all variants above are supported.
   *
   * @param groupId The unique SCIM group (subscription) ID.
   * @param requestBody
   * @param prefer If set to `return=minimal`, the server MAY return `204 No Content` on success.
   * @returns any Updated SCIM group object after patch (duplicates collapsed).
   * @throws ApiError
   */
  public static patchScimGroup(
    groupId: string,
    requestBody: {
      schemas: Array<string>;
      Operations: Array<{
        op: 'add' | 'remove' | 'replace';
        /**
         * Examples:
         * - members
         * - members[value eq "<user_id>"]
         *
         */
        path?: string;
        value?: Record<string, any> | string | boolean;
      }>;
    },
    prefer?: string,
  ): CancelablePromise<Record<string, any>> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/scim/v2/Groups/{group_id}',
      path: {
        group_id: groupId,
      },
      headers: {
        Prefer: prefer,
      },
      body: requestBody,
      mediaType: 'application/scim+json',
      errors: {
        400: `Invalid SCIM patch or invalid member (e.g., missing/invalid \`schemas\`, bad path/value, user not found, user not in org, or non-SCIM user).`,
        401: `Unauthorized - SCIM token missing.`,
        403: `Forbidden - Invalid SCIM token.`,
        404: `Group not found.`,
        409: `Conflict — user already assigned to a different group in the org, or insufficient licenses.`,
      },
    });
  }

  /**
   * Replace SCIM group members via PUT
   * * The SCIM Group `id` equals our internal `subscription.id`.
   * * **Schemas requirement:** Per RFC 7643 §3, the request body **MUST** include `schemas`
   * and it **MUST** contain the core Group schema URI
   * `urn:ietf:params:scim:schemas:core:2.0:Group`. Missing/invalid schemas result in `400`.
   * * **Content type:** Clients SHOULD send `Content-Type: application/scim+json`
   * and `Accept: application/scim+json`.
   * * **Capacity rule (projected):** For PUT, we validate the final projected
   * member count *after* accounting for removals:
   * `projected = current - remove + add`. We return `409` only if `projected > seats`.
   * This enables 1-for-1 swaps even when currently at capacity.
   * * **Add guards:** Each addition is protected by:
   * - an authoritative seat check, and
   * - a uniqueness check to prevent assigning the same user to different groups
   * in the same org, plus a numeric guard when seat count is known.
   * * **Full replace semantics:** PUT replaces the group's membership entirely.
   * If `members` is omitted or `[]`, all existing members are removed.
   * * **Idempotency & rollback:**
   * - Adding users who are already members is a no-op (de-duplicated by user id).
   * - On mid-operation failure, we best-effort roll back any users added by this request,
   * while keeping successful removals.
   * * **displayName:** Accepted for compatibility and returned in responses, but does not
   * change subscription identity.
   * * **Prefer header:** If the client sends `Prefer: return=minimal`, the server MAY respond
   * with `204 No Content`. The `Content-Type` header will still be set to `application/scim+json`.
   * * **IdP compatibility:** Some IdPs use PUT for Group Push updates (name and membership).
   *
   * @param groupId The unique SCIM group (subscription) ID.
   * @param requestBody
   * @param prefer If set to `return=minimal`, the server MAY return `204 No Content` on success.
   * @returns any Updated SCIM group object after full replace (duplicates collapsed).
   * @throws ApiError
   */
  public static putScimGroup(
    groupId: string,
    requestBody: {
      schemas: Array<string>;
      /**
       * Accepted for compatibility; does not change subscription identity.
       */
      displayName?: string;
      /**
       * Full replacement of membership. Omit or [] to clear all.
       */
      members?: Array<{
        /**
         * SCIM user ID
         */
        value?: string;
        /**
         * User email
         */
        display?: string;
      }>;
    },
    prefer?: string,
  ): CancelablePromise<Record<string, any>> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/scim/v2/Groups/{group_id}',
      path: {
        group_id: groupId,
      },
      headers: {
        Prefer: prefer,
      },
      body: requestBody,
      mediaType: 'application/scim+json',
      errors: {
        400: `Invalid request (e.g., missing/invalid \`schemas\`, bad members, user not found, user not in org, or non-SCIM user).`,
        401: `Unauthorized - SCIM token missing.`,
        403: `Forbidden - Invalid SCIM token.`,
        404: `Group not found.`,
        409: `Conflict — final projected members exceed seats, or user already assigned to a different group in the org.`,
      },
    });
  }

  /**
   * List SCIM groups
   * @param filter SCIM filter by exact displayName.
   * @param startIndex 1-based index of the first group to return (SCIM uses 1-based pagination; 0 is invalid).
   *
   * @param count Maximum number of results to return (pagination). If omitted, defaults to 100 and is clamped to a maximum of 1000.
   *
   * @returns any List of groups
   * @throws ApiError
   */
  public static getScimV2Groups1(
    filter?: string,
    startIndex: number = 1,
    count: number = 100,
  ): CancelablePromise<{
    schemas: Array<string>;
    totalResults: number;
    startIndex: number;
    itemsPerPage: number;
    Resources: Array<{
      schemas?: Array<string>;
      id?: string;
      displayName?: string;
      members?: Array<{
        value?: string;
        display?: string;
      }>;
      meta?: {
        resourceType?: string;
      };
    }>;
  }> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/scim/v2/Groups',
      query: {
        filter: filter,
        startIndex: startIndex,
        count: count,
      },
      errors: {
        400: `Invalid query parameters`,
        401: `Unauthorized - SCIM token missing`,
        403: `Forbidden - Invalid SCIM token`,
      },
    });
  }

  /**
   * Create a SCIM group (subscription)
   * Design notes:
   * - The SCIM Group `id` we return is the `subscription.id`. The IdP
   * will use this same ID for subsequent GET/PATCH/DELETE.
   * - This does **not** create a new Stripe/LIC subscription; it binds to an
   * **existing** subscription identified via `displayName`.
   * - Accepted displayName format (prefix optional):
   * LocalStack-{PLAN}-{EMULATOR}-{subscription_id}
   * Example: LocalStack-Enterprise-SNOWFLAKE-lsub_xxx
   *
   * Capacity rules and idempotency:
   * - Batch preflight validates **current + NEW adds <= seats** (when seats are known).
   * - Per-add guard uses an authoritative availability check; if capacity is exhausted,
   * the request fails with 409 and **no partial assignments** remain.
   * - Members that are **already assigned to this subscription** are treated as
   * **idempotent success/no-op** and are included in the response; they do **not**
   * consume seats again.
   * - Members assigned to a **different group/subscription** in the same org cause 409.
   *
   * Identity/origin:
   * - Only users with `origin == SCIM` are eligible.
   * - Users not in the organization are rejected.
   *
   * @param requestBody
   * @returns any Group created successfully
   * @throws ApiError
   */
  public static postScimGroup(requestBody: {
    /**
     * The SCIM group name.
     * Format WITHOUT prefix: PLAN-EMULATOR-ID (e.g., Enterprise-SNOWFLAKE-lsub_xxx).
     * The API accepts names with or without the "LocalStack-" prefix.
     *
     */
    displayName: string;
    members?: Array<{
      /**
       * SCIM user ID
       */
      value?: string;
      /**
       * User email address
       */
      display?: string;
    }>;
  }): CancelablePromise<{
    schemas: Array<string>;
    id: string;
    displayName: string;
    members: Array<{
      value?: string;
      display?: string;
    }>;
    meta: {
      resourceType?: string;
    };
  }> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/scim/v2/Groups',
      body: requestBody,
      mediaType: 'application/scim+json',
      errors: {
        400: `Bad request (invalid/missing displayName, invalid members, or non-SCIM users)`,
        404: `Subscription not found for displayName`,
        409: `Conflict:
                - Insufficient licenses to add the requested NEW member(s).
                - User already assigned to a different group in this organization.
                - (Note) Users already in this group are treated as success/no-op (no 409).
                `,
      },
    });
  }

  /**
   * Retrieve supported SCIM resource types
   * @returns any List of SCIM resource types supported by the service
   * @throws ApiError
   */
  public static getScimV2ResourceTypes(): CancelablePromise<{
    schemas: Array<string>;
    Resources: Array<{
      schemas?: Array<string>;
      id?: string;
      name?: string;
      endpoint?: string;
      description?: string;
      schema?: string;
      meta?: {
        resourceType?: string;
        location?: string;
      };
    }>;
  }> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/scim/v2/ResourceTypes',
    });
  }

  /**
   * Retrieve SCIM resource schemas
   * @returns any List of SCIM schemas supported by the service
   * @throws ApiError
   */
  public static getScimV2Schemas(): CancelablePromise<{
    schemas: Array<string>;
    Resources: Array<{
      id?: string;
      name?: string;
      description?: string;
      attributes?: Array<{
        name?: string;
        type?: string;
        required?: boolean;
        multiValued?: boolean;
        subAttributes?: Array<{
          name?: string;
          type?: string;
          required?: boolean;
        }>;
      }>;
      schemas?: Array<string>;
    }>;
  }> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/scim/v2/Schemas',
    });
  }

  /**
   * Describe SCIM capabilities of this service
   * @returns any Service provider configuration
   * @throws ApiError
   */
  public static getScimV2ServiceProviderConfig(): CancelablePromise<{
    schemas: Array<string>;
    patch: {
      supported?: boolean;
    };
    bulk: {
      supported?: boolean;
      maxOperations?: number;
      maxPayloadSize?: number;
    };
    filter: {
      supported?: boolean;
      maxResults?: number;
    };
    changePassword: {
      supported?: boolean;
    };
    sort: {
      supported?: boolean;
    };
    etag: {
      supported?: boolean;
    };
    authenticationSchemes: Array<{
      type?: string;
      name?: string;
      description?: string;
      specUri?: string;
      primary?: boolean;
    }>;
    meta: {
      resourceType?: string;
      location?: string;
    };
  }> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/scim/v2/ServiceProviderConfig',
    });
  }

  /**
   * Get SCIM user by ID
   * @param userId
   * @returns any SCIM user found
   * @throws ApiError
   */
  public static getScimV2Users(userId: string): CancelablePromise<{
    schemas?: Array<string>;
    id?: string;
    userName?: string;
    name?: {
      givenName?: string;
      familyName?: string;
    };
    emails?: Array<{
      value?: string;
      primary?: boolean;
    }>;
    meta?: {
      location?: string;
      created?: string;
      lastModified?: string;
      resourceType?: string;
    };
    externalId?: string;
  }> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/scim/v2/Users/{user_id}',
      path: {
        user_id: userId,
      },
      errors: {
        401: `Unauthorized - SCIM token missing`,
        403: `Forbidden - Invalid SCIM token`,
        404: `User not found`,
      },
    });
  }

  /**
   * Update SCIM user fields via PATCH (RFC 7644-compliant)
   * This endpoint allows SCIM clients to update SCIM-provisioned user records.
   *
   * Supported operations:
   * - Set `active: false` to deactivate a user and remove them from the organization
   * (reused shared logic also used by PUT).
   * - Update user attributes such as `name`, `emails`, etc.
   *
   * Limitations:
   * - Setting `active: true` is not allowed. Reactivating users via SCIM is unsupported.
   * - Patching `userName` and `externalId` is disallowed and will cause errors.
   * - SCIM invitations are not supported (all users must be added to the
   * organization via the LocalStack Console).
   * @param userId The ID of the user to patch.
   * @param requestBody
   * @returns void
   * @throws ApiError
   */
  public static patchScimUser(
    userId: string,
    requestBody: {
      schemas: Array<string>;
      Operations: Array<{
        /**
         * SCIM Patch operation type.
         */
        op: 'add' | 'remove' | 'replace';
        /**
         * Path to the field to patch, e.g. "active", "name.givenName", or "emails".
         * Patching "userName" and "externalId" is not supported.
         *
         */
        path?: string;
        value?: string | boolean | Record<string, any> | any[];
      }>;
    },
  ): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/scim/v2/Users/{user_id}',
      path: {
        user_id: userId,
      },
      body: requestBody,
      mediaType: 'application/scim+json',
      errors: {
        400: `Invalid SCIM patch.`,
        401: `Unauthorized - SCIM token missing`,
        403: `Forbidden - Invalid SCIM token`,
        404: `User not found.`,
        409: `Reactivating users via SCIM is not supported.`,
      },
    });
  }

  /**
   * Replace SCIM user (full overwrite) and support deactivation
   * Replaces all mutable fields of a SCIM-provisioned user (name, email, etc.).
   * Additionally, if the payload sets `active: false`, the user is removed from the
   * organization with the same semantics as PATCH deactivation:
   * - Prevent removing the last workspace admin
   * - Delete the membership and remove the user from the org
   * - Ensure the user always belongs to at least one org
   * - Clear workspace preference if it points at this org
   *
   * Reactivating users via SCIM (`active: true` on a currently-inactive user)
   * is not supported and returns 409. `active: true` on an already-active user
   * is a no-op.
   * Only users provisioned via SCIM can be modified here.
   * @param userId The unique SCIM user ID.
   * @param requestBody
   * @returns any User replaced successfully (and deactivated if requested); the SCIM user is returned.
   * @throws ApiError
   */
  public static putScimUser(
    userId: string,
    requestBody: {
      /**
       * Must include SCIM core schema URN.
       */
      schemas: Array<string>;
      /**
       * The user's unique identifier (typically an email address).
       */
      userName: string;
      name?: {
        givenName?: string;
        familyName?: string;
      };
      emails?: Array<{
        value: string;
        primary?: boolean;
        type?: string;
        display?: string;
      }>;
      /**
       * Optional external ID to associate with this user.
       */
      externalId?: string;
      /**
       * If false, the user is deactivated and removed from the organization
       * using the same semantics as PATCH (active:false).
       * If true on an already-active user it is a no-op; if true on a
       * currently-inactive user it is rejected with 409 (reactivation
       * via SCIM is not supported).
       *
       */
      active?: boolean;
    },
  ): CancelablePromise<{
    schemas: Array<string>;
    id: string;
    userName: string;
    name?: {
      givenName?: string;
      familyName?: string;
    };
    emails?: Array<{
      value?: string;
      primary?: boolean;
    }>;
    active?: boolean;
    externalId?: string;
    meta: {
      location?: string;
      created?: string;
      lastModified?: string;
      resourceType?: string;
    };
  }> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/scim/v2/Users/{user_id}',
      path: {
        user_id: userId,
      },
      body: requestBody,
      mediaType: 'application/scim+json',
      errors: {
        400: `Bad request`,
        401: `Unauthorized - SCIM token missing`,
        403: `Forbidden - Invalid SCIM token`,
        404: `User not found`,
        409: `Conflict (e.g., email uniqueness or last-admin constraint on deactivation)`,
      },
    });
  }

  /**
   * List SCIM users
   * @param filter SCIM filter to find a specific user by userName (email).
   * @param startIndex 1-based index of first user to return (pagination).
   * @param count Maximum number of results to return (pagination). If omitted, defaults to 100 and is clamped to a maximum of 1000.
   *
   * @returns any List of users
   * @throws ApiError
   */
  public static getScimV2Users1(
    filter?: string,
    startIndex: number = 1,
    count: number = 100,
  ): CancelablePromise<{
    schemas: Array<string>;
    totalResults: number;
    startIndex: number;
    itemsPerPage: number;
    Resources: Array<{
      schemas?: Array<string>;
      id?: string;
      userName?: string;
      name?: {
        givenName?: string;
        familyName?: string;
      };
      emails?: Array<{
        value?: string;
        primary?: boolean;
      }>;
      meta?: {
        location?: string;
        created?: string;
        lastModified?: string;
        resourceType?: string;
      };
      externalId?: string;
    }>;
  }> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/scim/v2/Users',
      query: {
        filter: filter,
        startIndex: startIndex,
        count: count,
      },
      errors: {
        400: `Invalid query parameters`,
        401: `Unauthorized - SCIM token missing`,
        403: `Forbidden - Invalid SCIM token`,
      },
    });
  }

  /**
   * Create a SCIM user
   * @param requestBody
   * @returns any Idempotent success (user already existed in this org with the same SCIM identity)
   * @throws ApiError
   */
  public static postScimUser(requestBody: {
    /**
     * The SCIM schema URNs for the user. Must include the SCIM core user schema.
     */
    schemas: Array<string>;
    /**
     * The user's unique identifier within the organization (typically an email address).
     * Must be unique within the org for a given IdP.
     * Note: the canonical email used for login/identity is taken from the first item
     * in the "emails" array.
     *
     */
    userName: string;
    name?: {
      givenName?: string;
      familyName?: string;
    };
    emails: Array<{
      /**
       * Authoritative email used for login and uniqueness checks.
       */
      value: string;
      primary?: boolean;
    }>;
    /**
     * Optional external ID echoed back in the response.
     */
    externalId?: string;
    /**
     * Ignored on create. Users are always created active.
     */
    active?: boolean;
  }): CancelablePromise<{
    schemas: Array<string>;
    id: string;
    userName: string;
    name?: {
      givenName?: string;
      familyName?: string;
    };
    emails?: Array<{
      value?: string;
      primary?: boolean;
    }>;
    active?: boolean;
    externalId?: string;
    meta: {
      location?: string;
      created?: string;
      lastModified?: string;
      resourceType?: string;
    };
  }> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/scim/v2/Users',
      body: requestBody,
      mediaType: 'application/scim+json',
      errors: {
        400: `Bad request`,
        401: `Unauthorized - SCIM token missing`,
        403: `Forbidden - Invalid SCIM token`,
        409: `Conflict - A user with this email or userName already exists either in the organization (with a different SCIM identity) or globally.
                `,
      },
    });
  }
}
